import { EtTransaction } from "../../types/LandToken";
import Dialog from "../Dialog/Dialog";
import { styled, Tooltip } from "@mui/material";
import { getEventName } from "../../pages/EcoBank/LatestTransaction";
import moment from "moment";
import { InfoRounded } from "@mui/icons-material";
import { toReadable } from "../../utils/methods";
import { AddressLink, CurrencyLabel, Text } from "../";
import { CURRENCY_NAME, EtTransactionType } from "../../utils/constants";
import { useEffect, useState } from "react";

const Badge = styled("div")`
  background: ${({ theme }) => theme.palette.background.paper};
  border-radius: 5px;
  padding: 2px 10px;
  width: max-content;
`;

type TransactionDetailsProps = {
  txn: EtTransaction;
  open: boolean;
  onClose(): void;
};
export const TransactionDetails = ({ open, txn, onClose }: TransactionDetailsProps) => {
  const { data } = txn;
  const {
    guardianAmount,
    guardianReward,
    trustAmount,
    trustReward,
    ecoFutureAmount,
    ecoFutureReward,
    guardian,
    trust,
    ecoFuture,
  } = data || {};

  const [gasPrice, setGasPrice] = useState("-");
  const [txnFee, setTxnFee] = useState("-");

  useEffect(() => {
    if (open) {
      getGas(txn.hash);
    }
  }, [open]);

  const getValue = () => {
    if (txn.type === EtTransactionType.CLAIM_FUNDS) {
      return (
        <>
          {(guardianAmount || guardianReward) && (
            <Text numeric sx={{ gap: "6px" }}>
              {guardian ? (
                <AddressLink address={guardian} type={"user"} long small />
              ) : (
                <>
                  <span style={{ width: 90 }}>Guardian</span> received{" "}
                </>
              )}
              {toReadable(guardianReward || guardianAmount, true, true, 2)} <CurrencyLabel currency={CURRENCY_NAME} />
            </Text>
          )}
          {(trustReward || trustAmount) && (
            <Text numeric sx={{ gap: "6px" }}>
              {trust ? (
                <AddressLink address={trust} type={"trust"} long small />
              ) : (
                <>
                  <span style={{ width: 90 }}>Trust</span> received{" "}
                </>
              )}
              {toReadable(trustReward || trustAmount, true, true, 2)} <CurrencyLabel currency={CURRENCY_NAME} />
            </Text>
          )}
          {(ecoFutureReward || ecoFutureAmount) && (
            <Text numeric sx={{ gap: "6px" }}>
              {ecoFuture ? (
                <AddressLink address={ecoFuture} type={"admin"} long small />
              ) : (
                <>
                  <span style={{ width: 90 }}>EcoFuture</span> received{" "}
                </>
              )}
              {toReadable(ecoFutureReward || ecoFutureAmount, true, true, 2)} <CurrencyLabel currency={CURRENCY_NAME} />
            </Text>
          )}
        </>
      );
    }
    if (txn.type === EtTransactionType.REWARD_CLAIMED) {
      if (data.isPatron) {
        return (
          <Text numeric>
            <AddressLink address={data.patron} type="user" long sx={{ mr: 3 }} /> received{" "}
            {toReadable(data.patronReward, true, true, 3)}
            <CurrencyLabel />
          </Text>
        );
      } else {
        return (
          <>
            <Text numeric>
              <AddressLink address={data.guardian} type="user" long sx={{ mr: 3 }} /> received{" "}
              {toReadable(data.guardianReward, true, true, 3)}
              <CurrencyLabel />
            </Text>
            <Text numeric>
              <AddressLink address={data.referralAddress} type="user" long sx={{ mr: 3 }} /> received{" "}
              {toReadable(data.referralReward, true, true, 3)}
              <CurrencyLabel />
            </Text>
          </>
        );
      }
    }
    const value = data?.amount || data?.endingPrice || data?.startingBid || data?.startingPrice;
    const isWei = value?.length <= 10;
    return value ? toReadable(value, isWei, true, 0, CURRENCY_NAME) + "" : "";
  };

  const getSender = () => {
    if (txn.type === EtTransactionType.REWARD_CLAIMED) {
      if (data.isPatron) {
        return <AddressLink long address={data.patron} type="user" />;
      } else {
        return <AddressLink long address={data.guardian} type="user" />;
      }
    }
    if (txn.type === EtTransactionType.UPDATED) {
      return <AddressLink long address={data.trust} type="trust" />;
    }
    if (txn.type === EtTransactionType.MINTED) {
      return <AddressLink long address={txn.from as string} type="trust" />;
    }
    return txn.from && <AddressLink long address={txn.from} type="user" />;
  };

  const getReceiver = () => {
    if (txn.type === EtTransactionType.UPDATED) {
      return <AddressLink long address={data.guardian} type="user" />;
    }
    if (txn.type === EtTransactionType.MINTED) {
      return <AddressLink long address={txn.to as string} type="user" />;
    }
    return txn.to && <AddressLink long address={txn.to} type="user" />;
  };

  const getGas = async (hash: string) => {
    try {
      if (hash) {
        console.log("Getting receipt for " + hash);
        // Use ethers BrowserProvider with window.ethereum from MetaMask SDK
        if (window.ethereum) {
          const { ethers } = await import('ethers');
          const provider = new ethers.BrowserProvider(window.ethereum);
          const receipt = await provider.getTransactionReceipt(hash);
          
          if (receipt && receipt.gasUsed && receipt.gasPrice) {
            const gasUsed = receipt.gasUsed;
            const effectiveGasPrice = receipt.gasPrice;
            // BigInt multiplication
            const fee = gasUsed * effectiveGasPrice;
            
            // Convert BigInt values to strings for formatting
            const gasPriceStr = effectiveGasPrice.toString();
            const feeStr = fee.toString();
            
            // Format values for display using ethers utility functions directly
            setGasPrice(`${ethers.formatUnits(gasPriceStr, 'gwei')} Gwei`);
            setTxnFee(`${ethers.formatEther(feeStr)} ETH`);
          }
        }
      }
    } catch (error: any) {
      console.log("Failed to get receipt", { error });
    }
  };

  return (
    <Dialog maxWidth={true} open={open} onClose={() => onClose()} title="Transaction details">
      <Content>
        <Row>
          <Cell>
            <Tooltip arrow placement="top" title="Transaction hash">
              <InfoRounded />
            </Tooltip>{" "}
            Transaction Hash
          </Cell>
          <Cell>{txn.hash}</Cell>
        </Row>
        <Row>
          <Cell>
            <Tooltip arrow placement="top" title="Transaction type">
              <InfoRounded />
            </Tooltip>{" "}
            Type
          </Cell>
          <Cell>
            <Badge>{getEventName(txn as any)}</Badge>
          </Cell>
        </Row>
        <Row>
          <Cell>
            <Tooltip arrow placement="top" title="Transaction status">
              <InfoRounded />
            </Tooltip>{" "}
            Status
          </Cell>
          <Cell>
            <Badge sx={{ background: "success.main" }}>Completed</Badge>
          </Cell>
        </Row>
        <Row>
          <Cell>
            <Tooltip arrow placement="top" title="Transaction status">
              <InfoRounded />
            </Tooltip>{" "}
            Transaction Fee
          </Cell>
          <Cell>
            <Text numeric>{txnFee}</Text>
          </Cell>
        </Row>
        <Row>
          <Cell>
            <Tooltip arrow placement="top" title="Transaction status">
              <InfoRounded />
            </Tooltip>{" "}
            Gas Price
          </Cell>
          <Cell>
            <Text numeric>{gasPrice}</Text>
          </Cell>
        </Row>
        <Row>
          <Cell>
            <Tooltip arrow placement="top" title="NFT ID">
              <InfoRounded />
            </Tooltip>{" "}
            EcoFuture ID
          </Cell>
          <Cell>{txn.nftId}</Cell>
        </Row>
        <Row>
          <Cell>
            <Tooltip arrow placement="top" title="Timestamp">
              <InfoRounded />
            </Tooltip>{" "}
            Timestamp
          </Cell>
          <Cell>
            <span>{moment(txn.createdAt).fromNow()}</span>
            <span>( {moment(txn.createdAt).format("YYYY/MM/DD HH:mm:ss")} )</span>
          </Cell>
        </Row>
        <Row>
          <Cell>
            <Tooltip arrow placement="top" title="Transation sender">
              <InfoRounded />
            </Tooltip>{" "}
            From
          </Cell>
          <Cell>{getSender()}</Cell>
        </Row>
        <Row>
          <Cell>
            <Tooltip arrow placement="top" title="Transaction result receiver">
              <InfoRounded />
            </Tooltip>{" "}
            To
          </Cell>
          <Cell>{getReceiver()}</Cell>
        </Row>
        <Row>
          <Cell>
            <Tooltip arrow placement="top" title="Value received/updated">
              <InfoRounded />
            </Tooltip>{" "}
            Value
          </Cell>
          <Cell>
            <Text numeric sx={{ display: "flex", flexDirection: "column" }}>
              {getValue()}
            </Text>
          </Cell>
        </Row>
      </Content>
    </Dialog>
  );
};

const Content = styled("div")`
  padding: 12px;
`;

const Row = styled("div")`
  display: flex;

  & > div:first-of-type {
    width: 30%;
  }

  @media (max-width: 1240px) {
    flex-direction: column;
    border-bottom: 1px solid #3d3d3d;
    margin-bottom: 12px;
    & > div:first-of-type {
      width: 100%;
    }
    & > div:last-of-type {
      display: block;
      text-overflow: ellipsis;
      overflow: hidden;
    }
  }
`;

const Cell = styled("div")`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 0 12px;
  margin-bottom: 12px;
`;
